import { Type, type Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationField,
  StringField,
  BooleanField,
  TextField,
} from '@ayocore/platform/common/helpers/schema-types';
import accountingLegalEntitySchemaDefinition from './legal-entity.schema';
import accountingCurrencySchemaDefinition from './currency.schema';

import { name as moduleName } from '../module.json';
/**
 * Банковские счета юридических лиц.
 *
 * Позволяет хранить несколько счетов для одного юрлица, включая валюту и
 * статус основного счёта.
 */
export const AccountingLegalEntityBankAccountSchema = Type.Object({
  ...SystemFields,

  legal_entity_id: RelationField(accountingLegalEntitySchemaDefinition, 'ID юридического лица', {
    description: 'Юрлицо, к которому относится банковский счёт.',
  }),

  bank_name: StringField({
    title: 'Банк',
    minLength: 1,
    maxLength: 255,
    description: 'Название обслуживающего банка.',
    error: 'Введите название банка',
  }),

  bank_bik: StringField({
    title: 'БИК',
    nullable: true,
    maxLength: 20,
    description: 'Банковский идентификационный код (БИК).',
    // error: 'БИК должен содержать не более 20 символов',
  }),

  account_number: StringField({
    title: 'Расчётный счёт',
    minLength: 8,
    maxLength: 64,
    description: 'Номер расчётного, валютного или иного банковского счета.',
    error: 'Введите номер счёта (минимум 8 символов)',
  }),

  correspondent_account: StringField({
    title: 'Корреспондентский счёт',
    nullable: true,
    maxLength: 64,
    description: 'Корреспондентский счёт банка (если требуется).',
  }),

  bank_inn: StringField({
    title: 'ИНН банка',
    nullable: true,
    maxLength: 20,
    description: 'ИНН обслуживающего банка.',
  }),

  bank_address: StringField({
    title: 'Адрес банка',
    nullable: true,
    maxLength: 500,
    description: 'Юридический адрес обслуживающего банка.',
  }),

  currency_id: RelationField(accountingCurrencySchemaDefinition, 'ID валюты счёта', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Валюта, в которой ведётся счёт.',
  }),

  is_default: BooleanField({
    title: 'Основной счёт',
    default: false,
    description: 'Флаг основного расчётного счёта для юрлица.',
  }),

  notes: TextField({
    title: 'Комментарий',
    nullable: true,
    description: 'Дополнительные сведения, например условия обслуживания.',
  }),
});

export type AccountingLegalEntityBankAccount = Static<
  typeof AccountingLegalEntityBankAccountSchema
>;

export type CreateLegalEntityBankAccount = Omit<
  AccountingLegalEntityBankAccount,
  'id' | 'uuid' | 'created_at' | 'updated_at' | 'sort_order'
>;

export type UpdateLegalEntityBankAccount = Partial<CreateLegalEntityBankAccount>;

export default defineSchema({
  moduleName,
  tableName: 'legal_entity_bank_accounts',
  schema: AccountingLegalEntityBankAccountSchema,
  additionalIndexes: [
    'legal_entity_id',
    {
      columns: ['legal_entity_id', 'is_default'],
      name: 'idx_bank_acc_legal_ent_id_is_def',
    },
  ],
  dependencies: [
    accountingLegalEntitySchemaDefinition.tableName,
    accountingCurrencySchemaDefinition.tableName,
  ],
});
